import type { BlockDefinition, VideoEmbedProps, VideoProvider } from '../types';
import { escapeHtml } from '../html';

/**
 * Universal video embed — paste a YouTube, Vimeo, TikTok, or Instagram Reel
 * URL and it figures out the right embed. Auto-responsive (16:9 default,
 * 9:16 portrait for Reels / TikTok). No JS — just an iframe.
 *
 * URL → embed mapping is conservative: if we can't parse it cleanly, we
 * fall back to a clickable thumbnail with a "watch" link so the block
 * never visibly breaks the page.
 */
export const videoEmbed: BlockDefinition<VideoEmbedProps> = {
  type: 'video-embed',
  meta: {
    label: 'Video',
    icon: '🎬',
    category: 'media',
    description: 'YouTube / Vimeo / TikTok / Instagram Reel — paste the URL.',
  },
  defaults: () => ({
    url: '',
    portrait: false,
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<VideoEmbedProps>;
    return {
      url: typeof i.url === 'string' ? i.url.slice(0, 2000) : '',
      portrait: !!i.portrait,
    };
  },
  render(props) {
    const parsed = parseVideoUrl(props.url);
    if (!parsed) {
      return `<div class="pb-block pb-video-embed" style="aspect-ratio:${props.portrait ? '9/16' : '16/9'};background:rgba(0,0,0,0.06);border-radius:var(--radius-card);display:flex;align-items:center;justify-content:center;color:var(--text);opacity:0.4;font-size:14px;">No video set</div>`;
    }

    const aspect = props.portrait ? '9/16' : '16/9';
    const maxWidth = props.portrait ? '320px' : '100%';

    return `<div class="pb-block pb-video-embed" style="display:flex;justify-content:center;">
  <div style="width:100%;max-width:${maxWidth};aspect-ratio:${aspect};border-radius:var(--radius-card);overflow:hidden;background:#000;">
    <iframe src="${escapeHtml(parsed.embedUrl)}"
            style="width:100%;height:100%;border:0;"
            allow="autoplay; encrypted-media; picture-in-picture; web-share"
            allowfullscreen
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
            title="Video"></iframe>
  </div>
</div>`;
  },
};

/**
 * Parse a video URL and produce a safe embed URL. Returns null when the
 * URL isn't from a supported provider — caller renders an empty-state.
 */
export function parseVideoUrl(raw: string): { provider: VideoProvider; embedUrl: string } | null {
  const url = (raw || '').trim();
  if (!url) return null;

  // YouTube — covers youtu.be, /watch?v=, /shorts/, /embed/, /v/.
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i,
  );
  if (yt) {
    return {
      provider: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${encodeURIComponent(yt[1]!)}?rel=0&modestbranding=1`,
    };
  }

  // Vimeo — /<id> with all-digit id (or a custom unlisted hash after).
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d{6,})(?:\/([a-zA-Z0-9]+))?/i);
  if (vimeo) {
    const id = vimeo[1]!;
    const hash = vimeo[2] ? `?h=${encodeURIComponent(vimeo[2])}` : '';
    return { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}${hash}` };
  }

  // TikTok — /@user/video/<id> or vm.tiktok.com/<id> (the latter is a redirect
  // we can't follow at render time, but the embed endpoint handles it).
  const tiktok = url.match(/tiktok\.com\/(?:@[^/]+\/video\/|v\/|embed\/)?(\d{6,})/i);
  if (tiktok) {
    return { provider: 'tiktok', embedUrl: `https://www.tiktok.com/embed/v2/${tiktok[1]}` };
  }

  // Instagram Reel — /reel/<shortcode>/ or /p/<shortcode>/.
  const reel = url.match(/instagram\.com\/(?:reel|reels|p|tv)\/([A-Za-z0-9_-]+)/i);
  if (reel) {
    return {
      provider: 'instagram-reel',
      embedUrl: `https://www.instagram.com/p/${encodeURIComponent(reel[1]!)}/embed/`,
    };
  }

  return null;
}
